import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Plus, Trash2, Printer, UtensilsCrossed, Pencil, ChevronDown, X, BookmarkPlus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { API } from '@/lib/api';

// Qué categoría del banco usar para cada tiempo de comida
const TIEMPO_CATEGORIA = {
  desayuno:    'desayuno',
  colacion_am: 'snack',
  comida:      'comida',
  colacion_pm: 'snack',
  cena:        'cena',
};

// Texto que se guarda en el plan al elegir un alimento: "Bistec a la mexicana (100 g)".
// Así la porción viaja con el platillo hasta el bot, la app y la impresión.
const textoAlimento = (food) => food.porcion ? `${food.nombre} (${food.porcion})` : food.nombre;

// Combobox con autocompletado: al escribir filtra el banco en tiempo real
export const AlimentoSelector = ({ tiempoKey, tiempoLabel, value, onChange, bancoPorCategoria, diasForm, diaActivo }) => {
  const [abierto, setAbierto] = useState(false);
  const categoria = TIEMPO_CATEGORIA[tiempoKey];
  const foods = bancoPorCategoria[categoria] || [];

  const usadosOtrosDias = new Set(
    Object.entries(diasForm)
      .filter(([dia]) => dia !== diaActivo)
      .map(([, diaData]) => diaData?.[tiempoKey])
      .filter(Boolean)
  );

  // Filtrar por lo que se va escribiendo — el input ES el buscador
  const filtrados = foods.filter(f =>
    !value || textoAlimento(f).toLowerCase().includes(value.toLowerCase())
  );

  const handleChange = (e) => {
    onChange(e.target.value);
    setAbierto(true);
  };

  const seleccionar = (food) => {
    onChange(textoAlimento(food));
    setAbierto(false);
  };

  const cerrar = () => setAbierto(false);

  return (
    <div className="space-y-1">
      <Label className="text-xs">{tiempoLabel}</Label>
      <div className="relative">
        <Input
          value={value}
          onChange={handleChange}
          onFocus={() => foods.length > 0 && setAbierto(true)}
          placeholder="Escribe para buscar o texto libre..."
          className="text-sm h-8 pr-7"
        />
        {/* Botón limpiar / chevron */}
        {value ? (
          <button
            type="button"
            onClick={() => { onChange(''); cerrar(); }}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground cursor-pointer"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        ) : foods.length > 0 && (
          <button
            type="button"
            onClick={() => setAbierto(v => !v)}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground cursor-pointer"
          >
            <ChevronDown className={cn('h-3.5 w-3.5 transition-transform duration-150', abierto && 'rotate-180')} />
          </button>
        )}

        {/* Dropdown de sugerencias */}
        {abierto && foods.length > 0 && (
          <>
            <div className="fixed inset-0 z-10" onClick={cerrar} />
            <div className="absolute z-20 top-full mt-1 left-0 right-0 min-w-[180px] bg-popover border border-border rounded-md shadow-lg overflow-hidden">
              <ul className="max-h-48 overflow-y-auto py-1">
                {filtrados.length === 0 ? (
                  <li className="px-3 py-2 text-xs text-muted-foreground italic">
                    Sin coincidencias — se guardará como texto libre
                  </li>
                ) : filtrados.map(food => {
                  const texto = textoAlimento(food);
                  const usado = usadosOtrosDias.has(texto);
                  const seleccionado = value === texto;
                  return (
                    <li
                      key={food.id}
                      onMouseDown={e => e.preventDefault()} // evita que el blur cierre antes del click
                      onClick={() => seleccionar(food)}
                      className={cn(
                        'flex items-center justify-between px-3 py-1.5 text-xs cursor-pointer transition-colors select-none',
                        seleccionado
                          ? 'bg-emerald-600/10 text-emerald-700 dark:text-emerald-400 font-medium'
                          : usado
                          ? 'text-muted-foreground/60 hover:bg-muted/50'
                          : 'hover:bg-accent hover:text-accent-foreground'
                      )}
                    >
                      <span>
                        {food.nombre}
                        {food.porcion && <span className="ml-1 text-muted-foreground">· {food.porcion}</span>}
                      </span>
                      <span className="flex items-center gap-2 flex-shrink-0 ml-2">
                        {food.calorias && (
                          <span className={cn('tabular-nums', seleccionado ? 'text-emerald-600' : 'text-muted-foreground')}>
                            {food.calorias} kcal
                          </span>
                        )}
                        {usado && <span className="text-muted-foreground/50 italic">otro día</span>}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export const DIAS = ['lunes', 'martes', 'miercoles', 'jueves', 'viernes', 'sabado', 'domingo'];
export const DIA_LABEL = { lunes: 'Lunes', martes: 'Martes', miercoles: 'Miércoles', jueves: 'Jueves', viernes: 'Viernes', sabado: 'Sábado', domingo: 'Domingo' };

// Quita tildes y pasa a minúsculas: 'Miércoles' -> 'miercoles'.
export const normKey = (s) => s.normalize('NFD').replace(new RegExp('[\u0300-\u036f]', 'g'), '').toLowerCase();
const _tieneComida = (dia) => dia && Object.values(dia).some(v => String(v ?? '').trim());

// Normaliza las llaves de días de un plan a su versión sin tilde. Los planes
// viejos se guardaron con tilde ('miércoles', 'sábado') y el frontend ahora usa
// llaves sin tilde, por lo que sin esto esos días se verían vacíos. Si dos
// llaves colapsan a la misma normalizada, gana la que tenga comida.
export const normPlanDias = (dias) => {
  const out = {};
  for (const [k, v] of Object.entries(dias || {})) {
    const c = normKey(k);
    if (!(c in out) || (_tieneComida(v) && !_tieneComida(out[c]))) out[c] = v;
  }
  return out;
};
export const TIEMPOS = [
  { key: 'desayuno',    label: 'Desayuno' },
  { key: 'colacion_am', label: 'Colación AM' },
  { key: 'comida',      label: 'Comida' },
  { key: 'colacion_pm', label: 'Colación PM' },
  { key: 'cena',        label: 'Cena' },
];

// Grupos del Sistema Mexicano de Alimentos Equivalentes (SMAE)
export const SMAE_GRUPOS = [
  { key: 'verduras',    label: 'Verduras' },
  { key: 'frutas',      label: 'Frutas' },
  { key: 'cereales',    label: 'Cereales y tubérculos' },
  { key: 'leguminosas', label: 'Leguminosas' },
  { key: 'aoa',         label: 'Alimentos de origen animal' },
  { key: 'leche',       label: 'Leche' },
  { key: 'grasas',      label: 'Aceites y grasas' },
  { key: 'azucares',    label: 'Azúcares' },
];

export const emptyDias = () =>
  Object.fromEntries(DIAS.map(d => [d, Object.fromEntries(TIEMPOS.map(t => [t.key, '']))]));

export const emptyEquivalentes = () =>
  Object.fromEntries(SMAE_GRUPOS.map(g => [g.key, '']));

const emptyForm = {
  nombre: '',
  fecha_inicio: new Date().toISOString().split('T')[0],
  modo: 'menu',
  calorias_objetivo: '',
  proteinas_g: '',
  carbohidratos_g: '',
  grasas_g: '',
  notas: '',
  dias: emptyDias(),
  equivalentes: emptyEquivalentes(),
};

const printPlan = (plan, patient, medicion) => {
  const w = window.open('', '_blank');
  if (!w) { alert('Permite ventanas emergentes para generar el PDF'); return; }

  const calcEdad = (fn) => {
    if (!fn) return null;
    const hoy = new Date(), nac = new Date(fn + 'T12:00:00');
    let e = hoy.getFullYear() - nac.getFullYear();
    if (hoy < new Date(hoy.getFullYear(), nac.getMonth(), nac.getDate())) e--;
    return e;
  };
  const calcIMC = (peso, estatura) => {
    if (!peso || !estatura) return null;
    return (peso / Math.pow(estatura / 100, 2)).toFixed(1);
  };
  const imcLabel = (v) => {
    if (!v) return '';
    const n = parseFloat(v);
    if (n < 18.5) return 'Bajo peso';
    if (n < 25)   return 'Peso normal';
    if (n < 30)   return 'Sobrepeso';
    if (n < 35)   return 'Obesidad I';
    if (n < 40)   return 'Obesidad II';
    return 'Obesidad III';
  };
  const objLabel = { bajar_peso: 'Pérdida de peso', control_enfermedad: 'Control de enfermedad', mantenimiento: 'Mantenimiento', otro: 'Otro' };
  const row = (l, v) => v ? `<div class="ir"><span class="il">${l}</span><span class="iv">${v}</span></div>` : '';

  const fechaEmision = new Date().toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' });
  const edad = calcEdad(patient.fecha_nacimiento);
  const imc  = calcIMC(patient.peso, patient.estatura);

  // Datos del paciente
  const infoRows = [
    row('Nombre', `${patient.nombre} ${patient.apellido}`),
    row('Edad', edad ? `${edad} años` : null),
    row('Objetivo', patient.objetivo ? (objLabel[patient.objetivo] || patient.objetivo) : null),
    row('Diagnóstico', patient.diagnostico_nutricional || null),
    row('Enf. crónicas', patient.enfermedades_cronicas?.length ? patient.enfermedades_cronicas.join(', ') : null),
    row('Alergias', patient.alergias_alimentarias?.length ? patient.alergias_alimentarias.join(', ') : null),
  ].join('');

  // Datos biométricos
  const bioRows = [
    row('Peso', patient.peso ? `${patient.peso} kg` : null),
    row('Talla', patient.estatura ? `${patient.estatura} cm` : null),
    row('IMC', imc ? `${imc} — ${imcLabel(imc)}` : null),
    row('Cintura / Cadera', medicion?.cintura && medicion?.cadera ? `${medicion.cintura} cm / ${medicion.cadera} cm` : null),
    row('Actividad física', patient.actividad_fisica || null),
    row('Sexo', patient.sexo || null),
  ].join('');

  // Mediciones corporales
  const medCampos = medicion ? [
    ['Cintura',    medicion.cintura,        'cm'],
    ['Cadera',     medicion.cadera,         'cm'],
    ['Brazo',      medicion.brazo,          'cm'],
    ['Muñeca',     medicion.muneca,         'cm'],
    ['% Grasa',    medicion.grasa_corporal, '%' ],
    ['Masa Musc.', medicion.masa_muscular,  '%' ],
    ['Peso',       medicion.peso,           'kg'],
  ].filter(([, v]) => v != null) : [];

  const medBoxes = medCampos.map(([lbl, val, unit]) =>
    `<div class="mbox"><div class="mv">${val}<span style="font-size:8px;font-weight:400;color:#6b7280;margin-left:1px">${unit}</span></div><div class="ml">${lbl}</div></div>`
  ).join('');

  // Macros pills
  const macros = [
    plan.calorias_objetivo  ? `<span class="pill"><span class="pv">${plan.calorias_objetivo}</span><span class="pl">kcal / día</span></span>` : '',
    plan.proteinas_g        ? `<span class="pill"><span class="pv">${plan.proteinas_g}g</span><span class="pl">Proteína</span></span>` : '',
    plan.carbohidratos_g    ? `<span class="pill"><span class="pv">${plan.carbohidratos_g}g</span><span class="pl">Carbohidratos</span></span>` : '',
    plan.grasas_g           ? `<span class="pill"><span class="pv">${plan.grasas_g}g</span><span class="pl">Grasas</span></span>` : '',
  ].filter(Boolean).join('');

  // Plan content
  let planHTML;
  if (plan.modo === 'equivalentes') {
    const filas = SMAE_GRUPOS
      .filter(g => plan.equivalentes?.[g.key] != null && plan.equivalentes[g.key] !== '')
      .map(g => `<tr><td class="tcol">${g.label}</td><td style="text-align:center;font-weight:700;color:#065f46;font-size:13px">${plan.equivalentes[g.key]}</td></tr>`)
      .join('');
    planHTML = `<table class="ptbl" style="width:55%"><thead><tr><th style="text-align:left">Grupo de Alimentos (SMAE)</th><th>Equivalentes / día</th></tr></thead><tbody>${filas}</tbody></table>`;
  } else {
    const filas = TIEMPOS.map(t => {
      const celdas = DIAS.map(d => {
        const v = plan.dias?.[d]?.[t.key] || '';
        return `<td>${v || '<span style="color:#d1fae5">—</span>'}</td>`;
      }).join('');
      return `<tr><td class="tcol">${t.label}</td>${celdas}</tr>`;
    }).join('');
    const headers = DIAS.map(d => `<th>${DIA_LABEL[d]}</th>`).join('');
    planHTML = `<table class="ptbl"><thead><tr><th style="background:#10b981;min-width:72px;text-align:left">Tiempo</th>${headers}</tr></thead><tbody>${filas}</tbody></table>`;
  }

  const html = `<!DOCTYPE html><html lang="es"><head>
<meta charset="utf-8">
<title>Plan Nutricional — ${patient.nombre} ${patient.apellido}</title>
<style>
@page{size:A4 landscape;margin:8mm 10mm}
*{margin:0;padding:0;box-sizing:border-box}
body{font-family:Arial,Helvetica,sans-serif;color:#111827;background:#fff;font-size:10px;line-height:1.4}
/* Header */
.hdr{background:linear-gradient(135deg,#064e3b 0%,#10b981 100%);color:#fff;padding:10px 16px;border-radius:8px;display:flex;justify-content:space-between;align-items:center;margin-bottom:10px}
.hl h1{font-size:20px;font-weight:800;letter-spacing:.3px}.hl p{font-size:9px;opacity:.8;letter-spacing:.5px;margin-top:2px}
.hc{text-align:center}.hc h2{font-size:15px;font-weight:700;letter-spacing:2px;text-transform:uppercase}.hc p{font-size:10px;opacity:.85;margin-top:2px}
.hr{text-align:right;font-size:9px;opacity:.9}.hr strong{display:block;font-size:11px;margin-bottom:2px}
/* Grid 2 col */
.g2{display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-bottom:8px}
/* Section header */
.sh{font-size:8px;font-weight:700;text-transform:uppercase;letter-spacing:1.2px;color:#065f46;border-bottom:2px solid #10b981;padding-bottom:2px;margin-bottom:5px}
/* Info box */
.ib{background:#f0fdf4;border:1px solid #d1fae5;border-radius:6px;padding:7px 10px}
.ir{display:flex;justify-content:space-between;padding:2.5px 0;border-bottom:1px dotted #d1fae5}.ir:last-child{border-bottom:none}
.il{color:#6b7280;font-size:9px}.iv{font-weight:600;color:#111827;font-size:9px;max-width:62%;text-align:right}
/* Mediciones */
.mrow{display:flex;gap:6px;margin-bottom:8px}
.mbox{flex:1;background:#f0fdf4;border:1px solid #d1fae5;border-radius:6px;padding:6px 4px;text-align:center}
.mbox .mv{font-size:14px;font-weight:700;color:#065f46;line-height:1.1}
.mbox .ml{font-size:8px;color:#6b7280;margin-top:2px}
/* Macros */
.macros{display:flex;gap:8px;flex-wrap:wrap;margin-bottom:8px}
.pill{background:#f0fdf4;border:1.5px solid #10b981;border-radius:20px;padding:4px 14px;display:inline-flex;gap:5px;align-items:baseline}
.pill .pv{font-size:14px;font-weight:700;color:#065f46}.pill .pl{font-size:8.5px;color:#6b7280}
/* Plan table */
.ptbl{width:100%;border-collapse:collapse;margin-bottom:8px}
.ptbl th{background:#065f46;color:#fff;padding:5px 4px;font-size:8px;text-transform:uppercase;letter-spacing:.5px;text-align:center;font-weight:600}
.ptbl td{padding:5px 5px;border:1px solid #d1fae5;vertical-align:top;line-height:1.35;font-size:9px}
.tcol{background:#ecfdf5!important;font-weight:700;color:#065f46;white-space:nowrap;border-right:2px solid #10b981!important;font-size:9px}
.ptbl tr:nth-child(even) td:not(.tcol){background:#fafef7}
/* Notes */
.notes{background:#f0fdf4;border:1px solid #d1fae5;border-radius:6px;padding:8px 10px;font-size:10px;line-height:1.5;color:#374151;margin-bottom:8px}
/* Footer */
.ftr{display:flex;justify-content:space-between;align-items:flex-end;border-top:1.5px solid #d1fae5;padding-top:6px;margin-top:4px}
.sig{width:160px;border-bottom:1px solid #9ca3af;height:22px;margin-bottom:3px}
/* Print */
.noprint{margin-top:16px;text-align:center}
@media print{.noprint{display:none!important}}
</style>
</head><body>

<div class="hdr">
  <div class="hl"><h1>Cl&#237;nica Dentu</h1><p>Nutrici&#243;n &middot; Bienestar &middot; Salud</p></div>
  <div class="hc"><h2>Plan Nutricional Semanal</h2><p>${plan.nombre}</p></div>
  <div class="hr"><span>Fecha de emisi&#243;n</span><strong>${fechaEmision}</strong><span>Inicio del plan: ${plan.fecha_inicio}</span></div>
</div>

<div class="g2">
  <div><div class="sh">Datos del Paciente</div><div class="ib">${infoRows || row('Sin datos adicionales','—')}</div></div>
  <div><div class="sh">Datos Biom&#233;tricos</div><div class="ib">${bioRows || row('Sin datos biom&#233;tricos','—')}</div></div>
</div>

${medCampos.length > 0 ? `
<div><div class="sh">Mediciones Corporales${medicion?.fecha ? ` &nbsp;&middot;&nbsp; ${medicion.fecha}` : ''}</div>
<div class="mrow">${medBoxes}</div></div>` : ''}

${macros ? `<div><div class="sh">Objetivos Nutricionales Diarios</div><div class="macros">${macros}</div></div>` : ''}

<div><div class="sh">Plan ${plan.modo === 'equivalentes' ? 'de Equivalentes (SMAE)' : 'Semanal de Alimentaci&#243;n'}</div>${planHTML}</div>

${plan.notas ? `<div><div class="sh">Observaciones y Recomendaciones</div><div class="notes">${plan.notas}</div></div>` : ''}

<div class="ftr">
  <div><div class="sig"></div><div style="font-size:8.5px;color:#6b7280">Firma y sello del Profesional de Nutrici&#243;n</div></div>
  <div style="text-align:center;font-size:8px;color:#9ca3af">Documento de car&#225;cter personal y confidencial</div>
  <div style="text-align:right"><div style="font-size:12px;font-weight:800;color:#065f46">Cl&#237;nica Dentu</div><div style="font-size:8.5px;color:#6b7280">Nutrici&#243;n Profesional &middot; ${fechaEmision}</div></div>
</div>

<div class="noprint">
  <button onclick="window.print()" style="background:#10b981;color:#fff;border:none;border-radius:8px;padding:10px 28px;font-size:14px;cursor:pointer;font-weight:700;margin-right:8px">Imprimir / Guardar PDF</button>
  <button onclick="window.close()" style="background:#f3f4f6;color:#374151;border:1px solid #d1d5db;border-radius:8px;padding:10px 20px;font-size:14px;cursor:pointer">Cerrar</button>
</div>

</body></html>`;

  w.document.write(html);
  w.document.close();
};

export const PlanAlimenticio = ({ patient }) => {
  const [planes, setPlanes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [activeDay, setActiveDay] = useState('lunes');
  const [bancoPorCategoria, setBancoPorCategoria] = useState({});
  const [ultimaMedicion, setUltimaMedicion] = useState(null);
  const [plantillas, setPlantillas] = useState([]);
  const [plantillaSel, setPlantillaSel] = useState('');

  const fetch = async () => {
    try {
      const [planesRes, medRes] = await Promise.allSettled([
        axios.get(`${API}/patients/${patient.id}/planes`),
        axios.get(`${API}/patients/${patient.id}/mediciones`),
      ]);
      if (planesRes.status === 'fulfilled') {
        // Normaliza las llaves de días al cargar: recupera la dieta de planes
        // viejos guardados con tilde ('miércoles'/'sábado'), que de otro modo
        // se verían vacíos. No reescribe la base; solo corrige la vista.
        setPlanes(planesRes.value.data.map(p => ({ ...p, dias: normPlanDias(p.dias) })));
      }
      else toast.error('Error al cargar planes');
      if (medRes.status === 'fulfilled') {
        const meds = medRes.value.data;
        if (meds.length > 0) setUltimaMedicion(meds[meds.length - 1]);
      }
    } finally { setLoading(false); }
  };

  useEffect(() => { fetch(); }, [patient.id]);

  // Cargar banco de alimentos una sola vez al montar
  useEffect(() => {
    axios.get(`${API}/alimentos`).then(res => {
      const agrupados = {};
      for (const a of res.data) {
        if (!agrupados[a.categoria]) agrupados[a.categoria] = [];
        agrupados[a.categoria].push(a);
      }
      setBancoPorCategoria(agrupados);
    }).catch(() => {});
    fetchPlantillas();
  }, []);

  const fetchPlantillas = () =>
    axios.get(`${API}/dietas-plantillas`).then(res => setPlantillas(res.data)).catch(() => {});

  const openNew = () => {
    setEditingPlan(null);
    setForm(emptyForm);
    setActiveDay('lunes');
    setPlantillaSel('');
    setDialogOpen(true);
  };

  // Precarga el form desde una dieta plantilla; fecha_inicio queda en hoy
  const aplicarPlantilla = (id) => {
    setPlantillaSel(id);
    if (!id) { setForm(emptyForm); return; }
    const p = plantillas.find(t => t.id === id);
    if (!p) return;
    setForm({
      nombre: p.nombre,
      fecha_inicio: new Date().toISOString().split('T')[0],
      modo: p.modo || 'menu',
      calorias_objetivo: p.calorias_objetivo ?? '',
      proteinas_g: p.proteinas_g ?? '',
      carbohidratos_g: p.carbohidratos_g ?? '',
      grasas_g: p.grasas_g ?? '',
      notas: p.notas ?? '',
      dias: { ...emptyDias(), ...normPlanDias(p.dias) },
      equivalentes: { ...emptyEquivalentes(), ...(p.equivalentes || {}) },
    });
    setActiveDay('lunes');
  };

  const guardarComoPlantilla = async (plan) => {
    const nombre = window.prompt('Nombre de la plantilla:', plan.nombre);
    if (!nombre?.trim()) return;
    try {
      await axios.post(`${API}/dietas-plantillas`, {
        nombre: nombre.trim(),
        modo: plan.modo || 'menu',
        calorias_objetivo: plan.calorias_objetivo,
        proteinas_g: plan.proteinas_g,
        carbohidratos_g: plan.carbohidratos_g,
        grasas_g: plan.grasas_g,
        dias: plan.dias || {},
        equivalentes: plan.equivalentes || {},
        notas: plan.notas,
      });
      toast.success('Plantilla guardada — disponible al crear nuevos planes');
      fetchPlantillas();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar plantilla');
    }
  };

  const eliminarPlantilla = async () => {
    const p = plantillas.find(t => t.id === plantillaSel);
    if (!p || !window.confirm(`¿Eliminar la plantilla "${p.nombre}"? Los planes ya creados no se afectan.`)) return;
    try {
      await axios.delete(`${API}/dietas-plantillas/${plantillaSel}`);
      setPlantillaSel('');
      fetchPlantillas();
      toast.success('Plantilla eliminada');
    } catch { toast.error('Error al eliminar plantilla'); }
  };

  const openEdit = (plan) => {
    setEditingPlan(plan);
    setForm({
      nombre: plan.nombre,
      fecha_inicio: plan.fecha_inicio,
      modo: plan.modo || 'menu',
      calorias_objetivo: plan.calorias_objetivo ?? '',
      proteinas_g: plan.proteinas_g ?? '',
      carbohidratos_g: plan.carbohidratos_g ?? '',
      grasas_g: plan.grasas_g ?? '',
      notas: plan.notas ?? '',
      dias: { ...emptyDias(), ...plan.dias },
      equivalentes: { ...emptyEquivalentes(), ...(plan.equivalentes || {}) },
    });
    setActiveDay('lunes');
    setDialogOpen(true);
  };

  const setDiaTiempo = (dia, tiempo, val) => {
    setForm(prev => ({
      ...prev,
      dias: { ...prev.dias, [dia]: { ...prev.dias[dia], [tiempo]: val } },
    }));
  };

  const setEquivalente = (grupo, val) => {
    setForm(prev => ({ ...prev, equivalentes: { ...prev.equivalentes, [grupo]: val } }));
  };

  const copyDayToAll = () => {
    const source = form.dias[activeDay];
    setForm(prev => ({
      ...prev,
      dias: Object.fromEntries(DIAS.map(d => [d, { ...source }])),
    }));
    toast.success(`Comidas de ${DIA_LABEL[activeDay]} copiadas a todos los días`);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nombre || !form.fecha_inicio) { toast.error('Nombre y fecha son obligatorios'); return; }
    const payload = {
      paciente_id: patient.id,
      ...form,
      calorias_objetivo: form.calorias_objetivo !== '' ? parseInt(form.calorias_objetivo) : null,
      proteinas_g:       form.proteinas_g       !== '' ? parseInt(form.proteinas_g)       : null,
      carbohidratos_g:   form.carbohidratos_g   !== '' ? parseInt(form.carbohidratos_g)   : null,
      grasas_g:          form.grasas_g          !== '' ? parseInt(form.grasas_g)          : null,
      // En modo equivalentes no se guardan días; en modo menú no se guardan equivalentes.
      dias: form.modo === 'menu' ? form.dias : {},
      equivalentes: form.modo === 'equivalentes'
        ? Object.fromEntries(
            Object.entries(form.equivalentes)
              .filter(([, v]) => v !== '' && v != null)
              .map(([k, v]) => [k, parseFloat(v)])
          )
        : {},
    };
    try {
      if (editingPlan) {
        await axios.put(`${API}/patients/${patient.id}/planes/${editingPlan.id}`, payload);
        toast.success('Plan actualizado');
      } else {
        await axios.post(`${API}/patients/${patient.id}/planes`, payload);
        toast.success('Plan creado');
      }
      setDialogOpen(false);
      fetch();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este plan alimenticio? Esta acción no se puede deshacer.')) return;
    try {
      await axios.delete(`${API}/patients/${patient.id}/planes/${id}`);
      setPlanes(prev => prev.filter(p => p.id !== id));
      toast.success('Plan eliminado');
    } catch { toast.error('Error al eliminar'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button size="sm" onClick={openNew}>
          <Plus className="h-4 w-4 mr-1" />
          Nuevo plan
        </Button>
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground text-center py-8">Cargando...</p>
      ) : planes.length === 0 ? (
        <Card className="bg-card border-border/50">
          <CardContent className="flex flex-col items-center py-14 text-muted-foreground">
            <UtensilsCrossed className="h-10 w-10 mb-3 opacity-30" />
            <p className="text-sm">Sin planes alimenticios</p>
            <p className="text-xs mt-1">Crea el primer plan semanal para este paciente</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {planes.map(plan => (
            <Card key={plan.id} className="bg-card border-border/50">
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <CardTitle className="text-base">{plan.nombre}</CardTitle>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Inicio: {plan.fecha_inicio}
                      {plan.modo === 'equivalentes' && ' · Equivalentes (SMAE)'}
                      {plan.calorias_objetivo && ` · ${plan.calorias_objetivo} kcal/día`}
                    </p>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => guardarComoPlantilla(plan)} title="Guardar como plantilla" aria-label="Guardar como plantilla">
                      <BookmarkPlus className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => printPlan(plan, patient, ultimaMedicion)} title="Imprimir" aria-label="Imprimir plan">
                      <Printer className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(plan)} aria-label="Editar plan">
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 hover:text-destructive" onClick={() => handleDelete(plan.id)} aria-label="Eliminar plan">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {plan.modo === 'equivalentes' ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
                    {SMAE_GRUPOS.filter(g => plan.equivalentes?.[g.key] != null && plan.equivalentes[g.key] !== '').map(g => (
                      <div key={g.key} className="flex items-center justify-between text-xs border-b border-border/40 py-1.5">
                        <span className="text-muted-foreground">{g.label}</span>
                        <span className="font-semibold">{plan.equivalentes[g.key]}</span>
                      </div>
                    ))}
                    {SMAE_GRUPOS.every(g => plan.equivalentes?.[g.key] == null || plan.equivalentes[g.key] === '') && (
                      <p className="text-xs text-muted-foreground opacity-60">Sin equivalentes definidos.</p>
                    )}
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr>
                          <th className="text-left p-2 text-muted-foreground font-medium w-24">Día</th>
                          {TIEMPOS.map(t => (
                            <th key={t.key} className="text-left p-2 text-muted-foreground font-medium">{t.label}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {DIAS.map(dia => (
                          <tr key={dia} className="border-t border-border/40">
                            <td className="p-2 font-medium text-xs">{DIA_LABEL[dia]}</td>
                            {TIEMPOS.map(t => (
                              <td key={t.key} className="p-2 text-muted-foreground align-top">
                                {plan.dias?.[dia]?.[t.key] || <span className="opacity-30">—</span>}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {plan.notas && <p className="text-xs text-muted-foreground mt-3 pt-3 border-t border-border/40">{plan.notas}</p>}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Dialog de creación/edición */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingPlan ? 'Editar plan' : 'Nuevo plan alimenticio'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-2">
            {/* Partir de una dieta plantilla (solo al crear) */}
            {!editingPlan && plantillas.length > 0 && (
              <div className="space-y-1">
                <Label className="text-xs">Partir de plantilla</Label>
                <div className="flex gap-2">
                  <select
                    value={plantillaSel}
                    onChange={e => aplicarPlantilla(e.target.value)}
                    className="flex-1 h-9 rounded-md border border-border bg-background px-2 text-sm"
                  >
                    <option value="">Empezar en blanco</option>
                    {plantillas.map(p => (
                      <option key={p.id} value={p.id}>
                        {p.nombre}{p.calorias_objetivo ? ` — ${p.calorias_objetivo} kcal` : ''}{p.modo === 'equivalentes' ? ' (SMAE)' : ''}
                      </option>
                    ))}
                  </select>
                  {plantillaSel && (
                    <Button type="button" variant="ghost" size="icon" className="h-9 w-9 hover:text-destructive"
                      onClick={eliminarPlantilla} title="Eliminar plantilla" aria-label="Eliminar plantilla">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                {plantillaSel && (
                  <p className="text-xs text-muted-foreground">
                    Ajusta porciones, calorías o comidas antes de crear el plan — la plantilla no se modifica.
                  </p>
                )}
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1 col-span-2 md:col-span-1">
                <Label>Nombre del plan *</Label>
                <Input placeholder="Plan semana 1" value={form.nombre}
                  onChange={e => setForm({ ...form, nombre: e.target.value })} />
              </div>
              <div className="space-y-1">
                <Label>Fecha inicio *</Label>
                <Input type="date" value={form.fecha_inicio}
                  onChange={e => setForm({ ...form, fecha_inicio: e.target.value })} />
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {[
                { key: 'calorias_objetivo', label: 'kcal/día' },
                { key: 'proteinas_g',       label: 'Proteína g' },
                { key: 'carbohidratos_g',   label: 'Carbos g' },
                { key: 'grasas_g',          label: 'Grasas g' },
              ].map(f => (
                <div key={f.key} className="space-y-1">
                  <Label className="text-xs">{f.label}</Label>
                  <Input type="number" min="0" value={form[f.key]}
                    onChange={e => setForm({ ...form, [f.key]: e.target.value })} />
                </div>
              ))}
            </div>

            {/* Selector de modalidad */}
            <div className="space-y-1">
              <Label className="text-xs">Modalidad del plan</Label>
              <div className="flex gap-2">
                {[
                  { v: 'menu',         label: 'Menú por días' },
                  { v: 'equivalentes', label: 'Equivalentes (SMAE)' },
                ].map(opt => (
                  <button key={opt.v} type="button" onClick={() => setForm({ ...form, modo: opt.v })}
                    className={`flex-1 py-2 rounded-lg border text-sm transition-colors ${
                      form.modo === opt.v
                        ? 'bg-emerald-600 text-white border-emerald-600'
                        : 'border-border text-muted-foreground hover:border-emerald-400'
                    }`}>
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Modo menú: tabs por día */}
            {form.modo === 'menu' && (
            <div>
              <div className="flex items-center gap-2">
                <div className="flex overflow-x-auto gap-1 pb-1 flex-1">
                  {DIAS.map(d => (
                    <button key={d} type="button" onClick={() => setActiveDay(d)}
                      className={`shrink-0 px-3 py-1.5 rounded-lg text-xs transition-colors ${
                        activeDay === d
                          ? 'bg-emerald-600 text-white'
                          : 'bg-muted hover:bg-muted/80 text-muted-foreground'
                      }`}>
                      {DIA_LABEL[d]}
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={copyDayToAll}
                  className="shrink-0 px-3 py-1.5 rounded-lg text-xs bg-muted hover:bg-muted/80 text-muted-foreground transition-colors whitespace-nowrap"
                  title={`Copiar comidas de ${DIA_LABEL[activeDay]} a todos los días`}
                >
                  Copiar a todos
                </button>
              </div>
              <div className="mt-3 space-y-3">
                {TIEMPOS.map(t => (
                  <AlimentoSelector
                    key={t.key}
                    tiempoKey={t.key}
                    tiempoLabel={t.label}
                    value={form.dias[activeDay]?.[t.key] ?? ''}
                    onChange={val => setDiaTiempo(activeDay, t.key, val)}
                    bancoPorCategoria={bancoPorCategoria}
                    diasForm={form.dias}
                    diaActivo={activeDay}
                  />
                ))}
              </div>
            </div>
            )}

            {/* Modo equivalentes: porciones por grupo (SMAE) */}
            {form.modo === 'equivalentes' && (
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground">
                  Equivalentes / porciones por grupo de alimentos al día.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {SMAE_GRUPOS.map(g => (
                    <div key={g.key} className="flex items-center gap-2">
                      <Label className="text-xs flex-1">{g.label}</Label>
                      <Input type="number" min="0" step="0.5" className="w-20"
                        value={form.equivalentes[g.key] ?? ''}
                        onChange={e => setEquivalente(g.key, e.target.value)} />
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-1">
              <Label>Notas generales</Label>
              <Textarea rows={2} value={form.notas}
                onChange={e => setForm({ ...form, notas: e.target.value })} />
            </div>

            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" className="flex-1">{editingPlan ? 'Guardar cambios' : 'Crear plan'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
